import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Copy, MoreHorizontal, Check } from "lucide-react";

interface LoginIdDisplayProps {
  loginIds: string[];
  userEmail: string;
}

export function LoginIdDisplay({ loginIds, userEmail }: LoginIdDisplayProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (loginId: string) => {
    await navigator.clipboard.writeText(loginId);
    setCopiedId(loginId);
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  const truncateId = (id: string, maxLength = 16) => {
    if (id.length <= maxLength) return id;
    return `${id.substring(0, maxLength)}...`;
  };

  // If no login IDs, show fallback based on email
  if (!loginIds.length) {
    const fallback = userEmail.substring(0, 12) + "...";
    return (
      <div className="flex min-w-0 items-center gap-1 overflow-hidden">
        <Badge variant="outline" className="truncate font-mono text-xs">
          {fallback}
        </Badge>
        <span className="text-muted-foreground whitespace-nowrap text-xs">
          (email)
        </span>
      </div>
    );
  }

  // Single login ID
  if (loginIds.length === 1) {
    const loginId = loginIds[0];
    return (
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="cursor-pointer font-mono text-xs hover:bg-secondary/80 dark:hover:bg-secondary/60"
              onClick={() => {
                void handleCopy(loginId);
              }}
            >
              {truncateId(loginId)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <code className="text-xs">{loginId}</code>
          </TooltipContent>
        </Tooltip>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 cursor-pointer p-0"
          onClick={() => {
            void handleCopy(loginId);
          }}
          title="Copy login ID"
        >
          {copiedId === loginId ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  }

  // Multiple login IDs
  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className="cursor-pointer font-mono text-xs hover:bg-secondary/80 dark:hover:bg-secondary/60"
            onClick={() => {
              void handleCopy(loginIds[0]);
            }}
          >
            {truncateId(loginIds[0])}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <code className="text-xs">{loginIds[0]}</code>
        </TooltipContent>
      </Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            title={`${loginIds.length} login IDs`}
          >
            <MoreHorizontal className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80" align="start">
          {loginIds.map((loginId) => (
            <DropdownMenuItem
              key={loginId}
              className="flex items-center justify-between gap-2 p-2"
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              <code className="flex-1 break-all text-xs">{loginId}</code>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 flex-shrink-0 cursor-pointer p-0"
                onClick={() => {
                  void handleCopy(loginId);
                }}
              >
                {copiedId === loginId ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
